import React, { FunctionComponent } from 'react';

import Flex from '@gojob/ui-bits/Flex';
import useTheme from '@gojob/ui-bits/styles/useTheme';
import Text from '@gojob/ui-bits/Text';

import { ArticleFragmentResponse } from '../../fragments/article';
import { ArticleCardFragmentResponse } from '../../fragments/article-card';
import Breadcrumbs from '../Article/Breadcrumbs';
import BlockRenderer from '../BlockRenderer';
import BreakpointContainer from '../BreakpointContainer';
import Buttons from '../Buttons';
import DateTime from './DateTime';
import FeaturedArticles from './FeaturedArticles';
import MainImage from './MainImage';
import TableOfContents from './TableOfContents';
import Tags from './Tags';

export interface ArticleProps extends ArticleFragmentResponse {
  featuredArticle?: {
    articles?: ArticleCardFragmentResponse[];
    title?: string;
    buttonLabel?: string;
  };
}

const Article: FunctionComponent<ArticleProps> = (props) => {
  const {
    featuredArticle,
    image,
    title = '',
    subtitle,
    publication_date,
    body,
    category,
    media,
    ctas = [],
  } = props;
  const { spacing } = useTheme();
  const gatsbyImageData = image?.asset?.localFile?.childImageSharp?.gatsbyImageData;

  return (
    <Flex
      css={{ boxSizing: 'border-box' }}
      width="100%"
      flexDirection="column"
      alignItems="center"
      padding={[8, 4, 4]}
    >
      <Flex width="100%">
        <Breadcrumbs category={category} title={title} />
      </Flex>
      <BreakpointContainer flexDirection="column" alignItems="stretch" maxWidth="lg" margin={[0, 0, 2]}>
        {gatsbyImageData ? <MainImage alt={title} image={gatsbyImageData} hotspot={image?.hotspot} /> : null}

        {category?.title ? <Tags tags={[category]} /> : null}

        <Text css={{ margin: spacing(1, 0) }} as="h1" variant="headline1">
          {title}
        </Text>

        {subtitle ? (
          <Text css={{ margin: spacing(1, 0) }} variant="message2">
            {subtitle}
          </Text>
        ) : null}

        <DateTime publicationDate={publication_date} media={media} />
      </BreakpointContainer>

      <TableOfContents blocks={body} />

      <BreakpointContainer margin={[2, 0, 0]} flexDirection="column" alignItems="stretch" maxWidth="lg">
        <BlockRenderer blocks={body} />
      </BreakpointContainer>

      <Buttons ctas={ctas} />

      <FeaturedArticles
        title={featuredArticle?.title}
        category={category}
        articles={featuredArticle?.articles || []}
        buttonLabel={featuredArticle?.buttonLabel}
      />
    </Flex>
  );
};

export default Article;
